import { InfoCircle } from 'iconsax-react';
import { FC, useState } from 'react';

interface IDetailElement {
  title: string;
  content?: string;
  onPress?: any;
  containerStyle?: string;
  tooltipContent?: string | object;
  showTooltip?: boolean;
}

export const DetailElement: FC<IDetailElement> = ({
  title,
  content,
  onPress,
  containerStyle,
  tooltipContent,
  showTooltip = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getMetricColor = (key: string, value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (key.includes('good')) return 'bg-green-600';
    if (key.includes('invalid') || key.includes('late')) {
      return numValue > 0 ? 'bg-red-800' : 'bg-gray-500';
    }
    if (key.includes('moderate')) {
      return numValue > 0 ? 'bg-yellow-400' : 'bg-gray-500';
    }
    if (key.includes('percent')) {
      if (numValue === 0) return 'bg-green-600';
      if (numValue <= 15) return 'bg-yellow-400';
      return 'bg-red-800';
    }
    return 'bg-blue-800';
  };

  const formatTooltipContent = (
    content: string | object
  ): Record<string, any> | null => {
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return typeof parsed === 'object' && parsed !== null ? parsed : null;
      } catch {
        return null;
      }
    }

    if (typeof content === 'object' && content !== null) {
      return content as Record<string, any>;
    }

    return null;
  };

  const renderMetricBadges = (
    metrics: Record<string, any> | null
  ): React.ReactNode => {
    if (!metrics || typeof metrics !== 'object') return null;

    return Object.entries(metrics).map(([key, value]) => {
      const displayKey = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      const colorClass = getMetricColor(key, value as number | string);

      return (
        <div key={key} className={`flex items-center text-xs font-medium mb-1`}>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 ${colorClass} rounded-full`} />
            <span>{`${displayKey} -`}</span>
          </div>
          <span className="ml-1 semibold">{value}</span>
        </div>
      );
    });
  };

  return (
    <div
      onClick={() => onPress && onPress()}
      className={containerStyle ?? 'w-full sm:w-4/12 mt-8 pr-5'}
    >
      <div className="flex items-center gap-2">
        <p className="medium text-neutral-500 text-sm">{title}</p>
        {showTooltip && tooltipContent && (
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Info Icon */}
            <InfoCircle className="cursor-pointer w-4 h-4 text-neutral-500 hover:text-neutral-600 rotate-[180deg]" />

            {/* Tooltip */}
            {isHovered && (
              <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-neutral-700 text-xs rounded-lg shadow-lg whitespace-pre-line w-[280px]">
                {renderMetricBadges(formatTooltipContent(tooltipContent))}
                {/* Arrow pointing down */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="mt-2 text-sm text-neutral-900 cursor-pointer">{content}</p>
    </div>
  );
};
